import { type ProductType } from 'src/components/Calculator/types';
import { type WebinarsCarouselProps } from 'src/components/Carousel/Carousels/WebinarCarousel';
import { type ClientCasesProps } from 'src/components/Carousel/types';
import { ContainersInstructionBannerData } from 'src/components/PageBlocks/helperComponents/Banner/ContainersInstruction';
import { FreeCoursesAndCertificationBannerData } from 'src/components/PageBlocks/helperComponents/Banner/FreeCoursesAndCertification';
import { type AboutProps } from 'src/uikit/About/types';
import { type AccordionProps } from 'src/uikit/Accordion/type';
import { type BenefitsProps } from 'src/uikit/Benefits/types';
import { type ReviewsProps } from 'src/uikit/Carousels/ReviewsCarousel';
import { type DocumentationProps } from 'src/uikit/Documentation/types';
import { type FullScreenProps } from 'src/uikit/FullScreen/type';
import { type PersonalManagerProps } from 'src/uikit/PersonalManager';
import { type TableProps } from 'src/uikit/Table';

import { ProductsListProps } from '../uikit/ProductsList';
import { CatalogProduct, ProductCategory } from './backendContent';

type AboutBlockType = {
  type: BlockTypes.About;
  data: AboutProps;
};

type BenefitsBlockType = {
  type: BlockTypes.BenefitsPreview | BlockTypes.BenefitsPrimary;
  data: BenefitsProps;
};

type CalculatorBlockType = {
  type: BlockTypes.Calculator;
  data: {
    id?: string;
    title?: string;
    isTariffPlans?: boolean;
    initialActiveProduct: ProductType;
  };
};

type DocumentationBlockType = {
  type: BlockTypes.Documentation;
  data: DocumentationProps;
};

type DemoSectionSingle = {
  type: BlockTypes.DemoSectionSingleImage | BlockTypes.DemoSectionSingleVideo;

  data: {
    id?: string;
    title: string;
    richText: string;
    image?: string;
    video?: string;
    button?: {
      title: string;
      link?: string;
    };
    contentLeft?: boolean;
  };
};

type DemoSectionTabs = {
  type:
    | BlockTypes.DemoSectionTabsImages
    | BlockTypes.DemoSectionTabsVideos
    | BlockTypes.DemoSectionTabsText;
  data: {
    id?: string;
    title: string;
    tabs: {
      title: string;
      image?: {
        title?: string;
        subtitle?: string;
        image: string;
      };
      video?: {
        link: string;
        controls?: boolean;
      };
      richText?: string;
      isDisabled?: boolean;
    }[];
    button?: {
      title: string;
      link?: string;
    };
  };
};

type FullscreenBlockType = {
  type: BlockTypes.FullScreenImage | BlockTypes.FullScreenVideo;
  data: FullScreenProps;
};

type AccordionBlockType = {
  type: BlockTypes.Accordion;
  data: AccordionProps;
};

export type CarouselBlockType = {
  type: BlockTypes.Carousel;
  data: WebinarsCarouselProps;
};

type TableBlockType = {
  type: BlockTypes.Table;
  data: TableProps;
};

type FeedbackCarousel = {
  type: BlockTypes.FeedbackCarousel;
  data: ReviewsProps;
};

type Consultation = {
  type: BlockTypes.Consultation;
  data: PersonalManagerProps;
};

type CasesCarousel = {
  type: BlockTypes.CasesCarousel;
  data: {
    id?: string;
    title?: string;
    cards?: ClientCasesProps[];
  };
};

export type ProductsList = {
  type: BlockTypes.ProductsList;
  data: {
    id?: string;
    products: string[];
    productsData: ProductsListProps;
    title?: string;
    description?: string;
    cardsWithShadow?: boolean;
  };
};

type BannerBlockData =
  | FreeCoursesAndCertificationBannerData
  | ContainersInstructionBannerData;

export type BannerBlock = {
  type: BlockTypes.Banner;
  data: BannerBlockData;
};

type PromoBanner = {
  type: BlockTypes.PromoBanner;
  data: {
    id?: string;
    title?: string;
    description?: string;
    buttons?: {
      title?: string;
      link?: string;
    }[];
  };
};

export type CatalogSectionContent = {
  type: BlockTypes.CatalogSection;
  data: {
    id?: string;
    title?: string;
    platforms?: string[];
    products?: CatalogProduct[];
    categories?: ProductCategory[];
  };
};

type AdvantagesCarousel = {
  type: BlockTypes.AdvantagesCarousel;
  data: {
    id?: string;
  };
};

export enum BlockTypes {
  About = 'About',
  Accordion = 'Accordion',
  AdvantagesCarousel = 'AdvantagesCarousel',
  Banner = 'Banner',
  BenefitsPreview = 'BenefitsPreview',
  BenefitsPrimary = 'BenefitsPrimary',
  Calculator = 'Calculator',
  Carousel = 'Carousel',
  CasesCarousel = 'CasesCarousel',
  CatalogSection = 'CatalogSection',
  Consultation = 'Consultation',
  DemoSectionSingleImage = 'DemoSectionSingleImage',
  DemoSectionSingleVideo = 'DemoSectionSingleVideo',
  DemoSectionTabsImages = 'DemoSectionTabsImages',
  DemoSectionTabsText = 'DemoSectionTabsText',
  DemoSectionTabsVideos = 'DemoSectionTabsVideos',
  DemoTabs = 'DemoSectionTabs',
  Documentation = 'Documentation',
  FeedbackCarousel = 'FeedbackCarousel',
  FullScreenImage = 'FullScreenImage',
  FullScreenVideo = 'FullScreenVideo',
  ProductsList = 'ProductsList',
  PromoBanner = 'PromoBanner',
  Table = 'Table',
}

export type ProductBlock =
  | AboutBlockType
  | AccordionBlockType
  | AdvantagesCarousel
  | BannerBlock
  | BenefitsBlockType
  | CalculatorBlockType
  | CarouselBlockType
  | CasesCarousel
  | CatalogSectionContent
  | Consultation
  | DemoSectionSingle
  | DemoSectionTabs
  | DocumentationBlockType
  | FeedbackCarousel
  | FullscreenBlockType
  | ProductsList
  | PromoBanner
  | TableBlockType;
